import { useState } from "react";
import { useEffect } from "react";
import { itemTypes, resolveItemTypeKey } from "data/item-types";
import { useSelector } from "react-redux";
import useCitationFields from "./use-citation-fields.hook";
import {
  fetchCitationMetaData,
  fetchSavedCitationMeta,
} from "features/Bookmarks/services/citation.service";
import axios from "axios";
import dayjs from "dayjs";
import { mapCitationData } from "data/citation";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { getRegularDate } from "utils/time.util";

const useCitationData = (
  setLoading,
  setArticleData,
  setCurrentItemType,
  setAuthors,
  setDate,
  setAccessDate,
  setCitation,
  setSelectedStyle,
  articleData,
  additionalData,
  setAdditionalData,
  selectedBookmarkOption,
  citationLoaded,
  setCitationLoaded,
  setOriginalArticleData,
  setOriginalAuthors,
  selectedStyle,
  currentItemType
) => {
  const [baseFields, setBaseFields] = useState({});
  const [fields, setFields] = useState({});
  const [requiredFields, setRequiredFields] = useState([]);
  const [softRequirementFields, setSoftRequirementFields] = useState([]);
  const [currentItemTypeState, setCurrentItemTypeState] = useState(null);

  const {
    summaryInfoData,
    collectionSourceId,
    isCitationAvailable,
    isReadOnlyView,
    itemTypes: loadedItemTypes,
  } = useSelector((state) => state.data);
  const { user } = useSelector((state) => state.auth);
  const { userPreferences } = useSelector((state) => state.account);
  const defaultStyle = userPreferences?.defaultCitationStyle || "apa";
  const { fetchFields } = useCitationFields();
  const { pathname } = useLocation();

  // EBSCO URLs are only filled in when the citation API returns them.
  const isEbscoUrl = (() => {
    try {
      return new URL(summaryInfoData.url).hostname.toLowerCase() === "search.ebscohost.com";
    } catch {
      return false;
    }
  })();


  // fetch saved page

  const findBaseFieldKeyByValue = (valueToFind) => {
    const baseFieldKey = Object.keys(baseFields).find(
      (key) => baseFields[key] === valueToFind
    );
    return baseFieldKey;
  };

  const getBaseFieldVal = (newBaseFields, field) => {
    const baseFieldKey = findBaseFieldKeyByValue(newBaseFields[field]);

    if (articleData[baseFieldKey]) {
      return articleData[baseFieldKey];
    }
    return additionalData[newBaseFields[field]];
  };

  const checkBaseField = (newBaseFields, field) => {
    return (
      newBaseFields[field] && findBaseFieldKeyByValue(newBaseFields[field])
    );
  };

  const resetAdditionalData = (newArticleData, fields, articleData) => {
    let newAdditionalData = { ...additionalData };
    Object.keys(articleData).forEach((key) => {
      if (!fields.hasOwnProperty(key) && !newArticleData.hasOwnProperty(key)) {
        newAdditionalData[key] = articleData[key];
      }
    });

    return newAdditionalData;
  };

  const resetArticleData = (
    newArticleData,
    fields,
    articleData,
    newBaseFields
  ) => {
    Object.keys(fields).forEach((field) => {
      if (articleData.hasOwnProperty(field)) {
        newArticleData[field] = articleData[field];
      } else if (checkBaseField(newBaseFields, field)) {
        newArticleData[field] = getBaseFieldVal(newBaseFields, field);
        additionalData[newBaseFields[field]] = newArticleData[field];
      } else if (additionalData.hasOwnProperty(field)) {
        newArticleData[field] = additionalData[field];
      } else {
        newArticleData[field] = "";
      }
    });

    const baseFieldArr = Object.values(baseFields);
    Object.keys(fields).forEach((field) => {
      if (baseFieldArr.includes(field)) {
        const baseKey = Object.keys(baseFields).find(
          (key) => baseFields[key] === field
        );
        newArticleData[field] = articleData[baseKey];
      }
    });
    setBaseFields(newBaseFields);
    return newArticleData;
  };

  const mapArticleData = (res, fields, currentItemTypeKey) => {
    let newArticleData = {},
      additionalData = {};
    newArticleData = resetArticleData(
      newArticleData,
      fields.fields,
      res,
      fields.baseFields
    );
    additionalData = resetAdditionalData(newArticleData, fields.fields, res);

    if (newArticleData.date) {
      newArticleData.date = getFormattedCitationDateValue(newArticleData.date);
    }
    if (newArticleData.accessDate) {
      // Strip time component from saved citation's accessDate_dtm before passing to DatePicker
      const cleanedAccessDate = typeof newArticleData.accessDate === "string"
        ? newArticleData.accessDate.split("T")[0]
        : newArticleData.accessDate;
      newArticleData.accessDate = cleanedAccessDate;
      setAccessDate(dayjs(cleanedAccessDate));
    }

    newArticleData.itemType =
      currentItemTypeKey || res.itemType || newArticleData.itemType || "webpage";

    setArticleData(newArticleData);
    setOriginalArticleData(newArticleData);
    setAdditionalData(additionalData);
  };

  async function mapBackendToFrontend(data, currentItemType, style) {
    const res = mapCitationData(data);
    const fields = await fetchFields(
      currentItemType,
      baseFields,
      setLoading,
      setFields,
      setRequiredFields,
      style || selectedStyle || defaultStyle
    );
    setSoftRequirementFields(fields.softRequiredFields || []);
    mapArticleData(res, fields, currentItemType);
    return res;
  }

  const fetchIfExistingCitation = async () => {
    try {
      let MetaData = await fetchSavedCitationMeta(collectionSourceId, user);
      const { data, authors } = setAuthorNames(MetaData);
      setOriginalAuthors(authors.map((author) => ({ ...author })));
      const currentItemType = setFetchedItemType(data);
      setCurrentItemTypeState(currentItemType);
      setSelectedStyle(data[0].styles_chr);
      setCitation(data[0].citation_chr);
      await mapBackendToFrontend(data[0], currentItemType.key, data[0].styles_chr);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setCitationLoaded(true);
    }
  };

  const setAuthorNames = (data) => {
    let authors = [];
    if (isCitationAvailable && data[0]?.creators?.length > 0) {
      authors = data[0]?.creators?.map((author) => {
        return {
          firstName: author.firstName_chr,
          lastName: author.lastName_chr,
          creatorType: "author",
          citationCreatorId: author.citationCreatorId_ids,
        };
      });
      data[0].creators = authors;
      setAuthors(authors);
    } else if (data[0]?.creators?.length > 0) {
      authors = data[0]?.creators?.map((author) => {
        if (author.name) {
          return {
            firstName: author.name,
            creatorType: "author",
          };
        }
        return {
          firstName: author.firstName,
          lastName: author.lastName,
          creatorType: "author",
        };
      });
      setAuthors(authors);
    }
    return { data, authors: [...authors] };
  };

  const setFetchedItemType = (data) => {
    const itemType = isCitationAvailable
      ? data[0].itemType_chr
      : data[0].itemType;
    const currentItemType = itemTypes.filter(
      (item) => item.key.toLowerCase() === itemType.toLowerCase()
    )[0];
    setCurrentItemType(resolveItemTypeKey(itemType, loadedItemTypes));
    return currentItemType;
  };

  const handleEmptyDataFields = (data, fields) => {
    if (!data.length || typeof data[0] !== "object" || data[0] === null) {
      data[0] = { itemType: "webpage", styles: selectedStyle || defaultStyle };
    }

    Object.keys(fields).forEach((field) => {
      if (!(field in data[0]) || !data[0][field]) {
        data[0][field] = field.toLocaleLowerCase().includes("date") ? null : "";
      }
    });

    return data;
  };

  const handleExternalArticleBaseFieldsData = (data, baseFields) => {
    const tempExternalData = {
      title: summaryInfoData.title,
      url: isEbscoUrl ? "" : summaryInfoData.url,
      publicationTitle: summaryInfoData.publisher,
      data: summaryInfoData.published_date,
    };

    for (const key in baseFields) {
      const mappedField = baseFields[key];

      if (tempExternalData[mappedField] && !data[0][key]) {
        data[0][key] = tempExternalData[mappedField];
      }
    }

    return data;
  };

  const handleExternalArticleData = (data, fields) => {
    if (summaryInfoData.title && fields.title) {
      if (!data[0]?.title)
        data[0] = {
          ...data[0],
          title: summaryInfoData.title,
        };
    }
    if (summaryInfoData.url && fields.url && !isEbscoUrl) {
      data[0] = {
        ...data[0],
        url: summaryInfoData.url,
      };
    }

    if (fields.publicationTitle && !data[0]?.publicationTitle && summaryInfoData.publisher) {
      data[0] = {
        ...data[0],
        publicationTitle: summaryInfoData.publisher,
      };
    }
    return data;
  };
  function getValidDate(dateString) {
    // Aggressively prune everything after the first three parts (Month Day Year)
    const cleanValue =
      dateString && typeof dateString === "string"
        ? dateString.split(/\s+/).slice(0, 3).join(" ").replace(/,/g, "").trim()
        : dateString;
    const date = new Date(cleanValue);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1970) {
      return null;
    } else {
      return date;
    }
  }

  function getFormattedCitationDateValue(dateValue) {
    if (!dateValue) return "";
    if (typeof dateValue === "object" && typeof dateValue.getMonth === "function") return getRegularDate(dateValue);

    if (typeof dateValue === "string") {
      // Strip time portion from ISO datetime strings like "2026-03-23T18:30:00.000Z"
      const stripped = dateValue.includes("T") ? dateValue.split("T")[0] : dateValue;
      const pruned = stripped.split(/\s+/).slice(0, 3).join(" ").replace(/,/g, "").trim();

      // ISO format YYYY-MM-DD or YYYY/MM/DD: parse manually to avoid timezone shifts
      if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(pruned)) {
        const [y, m, d] = pruned.split(/[-/]/);
        const date = new Date(Number(y), Number(m) - 1, Number(d));
        return !isNaN(date.getTime()) ? getRegularDate(date) : pruned;
      }

      // 3-part Month Day Year string (e.g. "Feb 3 2026") → safe to use new Date()
      // since an explicit Day is present (no risk of hallucinating Day=1)
      const parts = pruned.split(/\s+/);
      if (parts.length === 3) {
        const asDate = new Date(pruned);
        if (!isNaN(asDate.getTime()) && asDate.getFullYear() > 1970) {
          return getRegularDate(asDate);
        }
      }

      // 1 or 2-part strings like "March 2022" or "2026" → return as-is
      return pruned;
    }
    return String(dateValue);
  }

  const handleArticleDates = (data, fields) => {
    if (fields.date) {
      const pubDateVal = summaryInfoData.published_date;
      const pubDate = pubDateVal ? new Date(pubDateVal) : null;

      const resolvedDate =
        pubDate && !isNaN(pubDate.getTime()) && pubDate.getFullYear() > 1970
          ? pubDateVal
          : getValidDate(data[0].date)
            ? data[0].date
            : null;

      setDate(null);
      data[0].date = getFormattedCitationDateValue(resolvedDate);
    }
    if (fields.accessDate) setAccessDate(dayjs(data[0].accessDate));
  };

  const cleanPlaceholderData = (dataRow) => {
    const placeholder = "[Database Name]";
    const cleanRow = { ...dataRow };
    if (cleanRow.libraryCatalog === placeholder || cleanRow.libraryCatalog === "Database Name") {
      cleanRow.libraryCatalog = "";
    }
    if (cleanRow.archive === placeholder || cleanRow.archive === "Database Name") {
      cleanRow.archive = "";
    }
    return cleanRow;
  };

  const format_response = (data, currentItemTypeKey) => {
    setArticleData((prev) => {
      const cleanedData = cleanPlaceholderData(data[0]);
      const newState = {
        ...prev,
        ...cleanedData,
        itemType:
          currentItemTypeKey || cleanedData.itemType || prev.itemType || "webpage",
        date: getFormattedCitationDateValue(cleanedData.date),
        accessDate: getValidDate(cleanedData.accessDate),
      };
      return newState;
    });
  };

  const handleErrorCitationResponse = async (currentItemTypeKey) => {
    setCurrentItemType(resolveItemTypeKey(currentItemTypeKey, loadedItemTypes));
    const itemTypeObj = itemTypes.find(i => i.key.toLowerCase() === currentItemTypeKey.toLowerCase());
    setCurrentItemTypeState(itemTypeObj);
    let fields = await fetchFields(
      currentItemTypeKey,
      baseFields,
      setLoading,
      setFields,
      setRequiredFields,
      selectedStyle || defaultStyle
    );
    setBaseFields(fields.baseFields);
    setSoftRequirementFields(fields.softRequiredFields || []);
    let data = [{}];
    data = handleEmptyDataFields(data, fields.fields);
    data = handleExternalArticleData(data, fields.fields);
    data = handleExternalArticleBaseFieldsData(data, fields.baseFields);
    handleArticleDates(data, fields.fields);
    setArticleData({
      ...cleanPlaceholderData(data[0]),
      itemType: currentItemTypeKey || data[0].itemType || "webpage",
    });
  };

  const handleSuccessCitationResponse = async (data, currentItemType) => {
    setCurrentItemTypeState(currentItemType);
    let fields = await fetchFields(
      currentItemType.key,
      baseFields,
      setLoading,
      setFields,
      setRequiredFields,
      selectedStyle || defaultStyle
    );
    setBaseFields(fields.baseFields);
    setSoftRequirementFields(fields.softRequiredFields || []);

    data = handleEmptyDataFields(data, fields.fields);
    data = handleExternalArticleData(data, fields.fields);
    data = handleExternalArticleBaseFieldsData(data, fields.baseFields);
    handleArticleDates(data, fields.fields);

    format_response(data, currentItemType.key);
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchCitationData = async () => {
      setLoading(true);
      setCitationLoaded(false);

      if (isCitationAvailable) {
        fetchIfExistingCitation();
        return;
      }

      // A teacher/read-only viewer shouldn't trigger citation generation for a
      // bookmark the student never generated one for — fetching metadata here
      // is what feeds the auto-generate effect in Citation/index.jsx.
      if (isReadOnlyView) {
        setLoading(false);
        setCitationLoaded(true);
        return;
      }

      try {
        const metaData = await fetchCitationMetaData(
          signal,
          summaryInfoData.url
        );

        if (!metaData[0].itemType) {
          throw new Error("Item type is missing in the metadata.");
        }

        const { data, authors } = setAuthorNames(metaData);

        const currentItemType = setFetchedItemType(data);

        await handleSuccessCitationResponse(data, currentItemType);
      } catch (err) {
        if (axios.isCancel(err)) {
          console.error("Request canceled", err.message);
        } else {
          await handleErrorCitationResponse("webpage");
        }
      } finally {
        setLoading(false);
        setCitationLoaded(true);
      }
    };

    if (selectedBookmarkOption === "citation") fetchCitationData();

    return () => controller.abort();
  }, [selectedBookmarkOption, collectionSourceId]);


  // Re-fetch fields when style changes
  useEffect(() => {
    if (citationLoaded && currentItemType) {
      const updateFields = async () => {
        const fields = await fetchFields(
          currentItemType,
          baseFields,
          setLoading,
          setFields,
          setRequiredFields,
          selectedStyle || defaultStyle
        );
        setSoftRequirementFields(fields.softRequiredFields || []);
      };
      updateFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStyle]);

  return {
    baseFields,
    fields,
    requiredFields,
    softRequirementFields,
    setRequiredFields,
    setFields,
    setBaseFields,
    resetArticleData,
    resetAdditionalData,
    setSoftRequirementFields,
  };
};

export default useCitationData;
